import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { apiFetch } from '../auth/apiClient';
import { buildNotificationTargetPath } from '../utils/notificationTarget';
import { BoardItem } from '@cloudscape-design/board-components';
import { Header, ButtonDropdown, Table, StatusIndicator, Box, Spinner, TextFilter, SpaceBetween, Link, Button, Badge, Hotspot } from '@cloudscape-design/components';
import ApplicationEventsHelp from '../helpPanelContents/applicationEventsHelp';
import { formatReminderBusinessDate, getReminderBusinessDayDiffDays } from '../lib/reminderBusinessDay';

const STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  in_review: 'In Review',
  'in review': 'In Review',
  awaiting_applicant: 'Awaiting Applicant',
  on_hold: 'On Hold',
  'on hold': 'On Hold',
  docs_requested: 'Action Required',
  'docs requested': 'Action Required',
  action_required: 'Action Required',
  closure_notice: 'Closure Notice',
  'closure notice': 'Closure Notice',
  pending_approval: 'Pending Decision',
  pending_decision: 'Pending Decision',
  decision_ready: 'Decision Ready (Legacy)',
  decision_recorded: 'Decision Recorded',
  intake: 'Intake',
  initiated: 'Initiated',
  approved: 'Approved',
  completed: 'Completed',
  denied: 'Denied',
  rejected: 'Denied',
  closed: 'Closed',
  withdrawn: 'Withdrawn',
  archived: 'Archived'
};

const trimValue = (value) => (typeof value === 'string' ? value.trim() : '');

const isEmail = (value) => /^[^@\s]+@[^@\s]+$/.test(value);

const selectFirst = (values, predicate = null) => {
  for (const value of values) {
    const trimmed = trimValue(value);
    if (!trimmed) continue;
    if (predicate && !predicate(trimmed)) continue;
    return trimmed;
  }
  return '';
};

const normalizeStatusLabel = (value) => {
  const token = trimValue(value);
  if (!token) return '';
  const normalized = token.toLowerCase().replace(/\s+/g, '_');
  if (STATUS_LABELS[normalized]) {
    return STATUS_LABELS[normalized];
  }
  const words = normalized.split('_').filter(Boolean);
  if (!words.length) return '';
  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const ensureSentence = (text) => {
  const trimmed = trimValue(text);
  if (!trimmed) return '';
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

const truncate = (text, limit = 160) => {
  const value = trimValue(text);
  if (!value) return '';
  if (value.length <= limit) return value;
  return `${value.slice(0, limit).trim()}…`;
};

const REVIEW_NOTE_EVENT_PREFIXES = [
  'rm_review_',
  'nwac_review_',
  'intervention_proposal_',
  'intervention_revision_'
];

const getReviewEventNote = (payload = {}, eventType = '') => {
  const explicit = selectFirst([
    payload.review_note,
    payload.decision_notes,
    payload.decision_note,
    payload.note
  ]);
  if (explicit) return explicit;
  const normalizedEventType = trimValue(eventType);
  const isReviewEvent = REVIEW_NOTE_EVENT_PREFIXES.some(prefix => normalizedEventType.startsWith(prefix));
  return isReviewEvent ? selectFirst([payload.reason]) : '';
};

const appendReviewEventNote = (base, payload = {}, eventType = '') => {
  const baseText = trimValue(base);
  const note = truncate(getReviewEventNote(payload, eventType), 240);
  if (!baseText || !note) return baseText;
  if (baseText.toLowerCase().includes(note.toLowerCase())) return baseText;
  return `${ensureSentence(baseText)} Note: ${note}`;
};

const EVENT_TIMEZONE = 'America/Toronto';
const formatDateTime = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: EVENT_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  } catch (_) {
    return date.toLocaleString();
  }
};

const formatReminderDetails = (payload = {}, fallbackTitle) => {
  const title = trimValue(payload.title) || fallbackTitle || 'Reminder';
  const due = formatReminderBusinessDate(payload.due_at || payload.dueAt);
  const trackingId = payload.tracking_id || payload.application_id || '';
  const caseId = payload.case_id || '';
  const category = trimValue(payload.category);
  const description = truncate(payload.description || payload.note || payload.body);
  const parts = [`Reminder due: ${title}`];
  if (due) parts.push(`due ${due}`);
  if (category) parts.push(`Category: ${category}`);
  if (trackingId) parts.push(`Tracking ID ${trackingId}`);
  if (caseId) parts.push(`Case ${caseId}`);
  if (description) parts.push(`Note: ${description}`);
  return parts.join(' • ');
};

const formatActorDisplay = (event) => {
  if (!event) return '';
  const payload = event.event_data || {};
  // Resolve one actor source at a time. Assignment recipients and submitters
  // describe the event's subject, not necessarily the person taking the action.
  const sources = [
    {
      names: [event.actor?.displayName, event.user_name],
      emails: [event.actor?.email, event.actor_email, event.actor?.displayName, event.user_name]
    },
    { names: [payload.actor_name], emails: [payload.actor_email, payload.actor_name] }
  ];
  for (const source of sources) {
    const name = selectFirst(source.names, value => !isEmail(value));
    const email = selectFirst(source.emails, isEmail);
    if (name && email) return `${name} (${email})`;
    if (name || email) return name || email;
  }
  return '';
};

const formatEventMessage = (event, actorDisplay) => {
  const payload = event.event_data || {};
  const actorSuffix = actorDisplay ? ` by ${actorDisplay}` : '';

  switch (event.event_type) {
    case 'status_changed': {
      const fromLabel = normalizeStatusLabel(payload.from);
      const toLabel = normalizeStatusLabel(payload.to) || 'Unknown status';
      const base = fromLabel && toLabel && fromLabel !== toLabel
        ? `Status changed from ${fromLabel} to ${toLabel}`
        : `Status updated to ${toLabel}`;
      return ensureSentence(`${base}${actorSuffix}`);
    }
    case 'document_request_set': {
      const sourceRaw = trimValue(payload.source);
      const sourceLabel = sourceRaw ? sourceRaw.replace(/_/g, ' ') : '';
      const baseMessage = trimValue(payload.message) || 'Documents requested';
      const trimmedBase = baseMessage.replace(/[.!?]+$/, '');
      const base = sourceLabel && !baseMessage.toLowerCase().includes(sourceLabel)
        ? `${trimmedBase} (source: ${sourceLabel})`
        : baseMessage;
      return ensureSentence(actorSuffix ? `${base}${actorSuffix}` : base);
    }
    case 'document_request_cleared': {
      const sourceRaw = trimValue(payload.source);
      const sourceLabel = sourceRaw ? sourceRaw.replace(/_/g, ' ') : '';
      const baseMessage = trimValue(payload.message) || 'Document request cleared';
      const trimmedBase = baseMessage.replace(/[.!?]+$/, '');
      const base = sourceLabel && !baseMessage.toLowerCase().includes(sourceLabel)
        ? `${trimmedBase} (source: ${sourceLabel})`
        : baseMessage;
      return ensureSentence(actorSuffix ? `${base}${actorSuffix}` : base);
    }
    case 'document_request_reminder_due': {
      const base = trimValue(payload.message) || 'Document request reminder due';
      return ensureSentence(actorSuffix ? `${base}${actorSuffix}` : base);
    }
    case 'document_request_closure_due': {
      const base = trimValue(payload.message) || 'Document request closure due';
      return ensureSentence(actorSuffix ? `${base}${actorSuffix}` : base);
    }
    case 'case_assigned': {
      const toAssignee = trimValue(payload.to_assignee_name) || trimValue(payload.to_assignee_email) || 'assigned staff member';
      const fromAssignee = trimValue(payload.from_assignee_name) || trimValue(payload.from_assignee_email);
      let base = `Case assigned to ${toAssignee}`;
      if (fromAssignee) {
        base += ` (previously ${fromAssignee})`;
      }
      return ensureSentence(actorSuffix ? `${base}${actorSuffix}` : base);
    }
    case 'case_reassigned': {
      const toAssignee = trimValue(payload.to_assignee_name) || trimValue(payload.to_assignee_email) || 'new assignee';
      const fromAssignee = trimValue(payload.from_assignee_name) || trimValue(payload.from_assignee_email) || 'previous assignee';
      const base = `Case reassigned from ${fromAssignee} to ${toAssignee}`;
      return ensureSentence(actorSuffix ? `${base}${actorSuffix}` : base);
    }
    case 'assessment_submitted': {
      const name = actorDisplay;
      const base = 'Assessment submitted';
      return ensureSentence(name ? `${base} by ${name}` : base);
    }
    case 'assessment_recalled': {
      const base = trimValue(payload.message) || 'Assessment submission recalled before decision';
      return ensureSentence(actorSuffix ? `${base}${actorSuffix}` : base);
    }
    case 'message_deleted': {
      const base = trimValue(payload.message) || 'Secure message withdrawn';
      return ensureSentence(actorSuffix ? `${base}${actorSuffix}` : base);
    }
    case 'nwac_review_submitted': {
      const reviewer = actorDisplay;
      const outcomeRaw = trimValue(payload.outcome);
      const outcome = outcomeRaw ? outcomeRaw.replace(/_/g, ' ') : '';
      const reason = trimValue(payload.reason);
      const parts = ['NWAC review'];
      if (outcome) parts.push(`(${outcome})`);
      let base = parts.join(' ');
      if (reason) base += `: ${reason}`;
      if (reviewer) base = `${base} by ${reviewer}`;
      return ensureSentence(appendReviewEventNote(base, payload, event.event_type));
    }
    case 'reminder_created': {
      const title = trimValue(payload.title) || 'Reminder';
      const due = formatReminderBusinessDate(payload.due_at || payload.dueAt);
      const base = due ? `${title} (due ${due})` : title;
      return ensureSentence(actorSuffix ? `${base}${actorSuffix}` : base);
    }
    case 'reminder_due': {
      const base = formatReminderDetails(
        { ...payload, tracking_id: event.tracking_id, case_id: event.case_id }
      );
      return ensureSentence(actorSuffix ? `${base}${actorSuffix}` : base);
    }
    case 'reminder_overdue': {
      const title = trimValue(payload.title) || 'Reminder';
      const due = formatReminderBusinessDate(payload.due_at || payload.dueAt) || 'previously';
      const daysOverdue = (() => {
        if (Number.isFinite(payload.overdue_days) && payload.overdue_days > 0) return Math.floor(payload.overdue_days);
        if (Number.isFinite(payload.overdueDays) && payload.overdueDays > 0) return Math.floor(payload.overdueDays);
        const diff = getReminderBusinessDayDiffDays(payload.due_at || payload.dueAt, new Date());
        if (diff === null) return null;
        return diff > 0 ? diff : null;
      })();
      const overdueLabel = daysOverdue ? `${daysOverdue} day${daysOverdue === 1 ? '' : 's'} overdue` : null;
      const base = `Reminder overdue: ${title} (was due ${due}${overdueLabel ? `, ${overdueLabel}` : ''})`;
      const detail = truncate(payload.description || payload.body || payload.note);
      const trackingId = payload.tracking_id || payload.application_id || event.tracking_id;
      const caseId = payload.case_id || event.case_id;
      const parts = [base];
      if (trackingId) parts.push(`Tracking ID ${trackingId}`);
      if (caseId) parts.push(`Case ${caseId}`);
      if (detail) parts.push(`Note: ${detail}`);
      const message = parts.join(' • ');
      return ensureSentence(actorSuffix ? `${message}${actorSuffix}` : message);
    }
    case 'reminder_completed': {
      const title = trimValue(payload.title) || 'Reminder';
      const base = `Reminder completed: ${title}`;
      return ensureSentence(actorSuffix ? `${base}${actorSuffix}` : base);
    }
    case 'application_submitted': {
      const submitter = actorDisplay;
      const source = trimValue(payload.intake_source).replace(/_/g, ' ');
      const sourceNotes = trimValue(payload.intake_source_notes);
      const isManual = trimValue(payload.origin_channel) === 'admin_manual';
      const parts = ['Application submitted'];
      if (submitter) parts.push(`by ${submitter}`);
      if (isManual && source) parts.push(`(intake source: ${source})`);
      if (isManual && sourceNotes) parts.push(`Notes: ${sourceNotes}`);
      return ensureSentence(parts.join(' '));
    }
    case 'escalation_created':
    case 'escalation_escalated':
    case 'escalation_responded':
    case 'escalation_resolved': {
      const target = trimValue(payload.targetEmail || payload.target_email || payload.ownerEmail || payload.ownerRole);
      const stateLabel = trimValue(payload.state || event.event_type.replace('escalation_', '').replace(/_/g, ' ')) || 'escalation';
      const note = truncate(payload.reason || payload.details || payload.note || payload.last_action_note);
      const parts = [`Application escalated (${stateLabel})`];
      if (target) parts.push(`to ${target}`);
      if (note) parts.push(`Note: ${note}`);
      return ensureSentence(parts.join(' • '));
    }
    default:
      if (payload.message) return ensureSentence(appendReviewEventNote(payload.message, payload, event.event_type));
      if (payload.summary) return ensureSentence(appendReviewEventNote(payload.summary, payload, event.event_type));
      return '';
  }
};

const decorateEvent = (event) => {
  const actorDisplay = formatActorDisplay(event);
  return {
    ...event,
    actorDisplay,
    reminderDetails: formatReminderDetails(
      {
        ...event.event_data,
        tracking_id: event.tracking_id,
        case_id: event.case_id
      },
      event.event_data?.title
    ),
    displayMessage: formatEventMessage(event, actorDisplay)
  };
};

const ApplicationEvents = ({ actions, caseData, toggleHelpPanel, metadata = {} }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filteringText, setFilteringText] = useState('');
  const [sortingColumn, setSortingColumn] = useState({ sortingField: 'created_at' });
  const [isDescending, setIsDescending] = useState(true);
  const [csvGenerating, setCsvGenerating] = useState(false);

  const caseId = caseData?.id || caseData?.case_id || null;
  const HelpComponent = metadata?.helpComponent || ApplicationEventsHelp;
  const helpTitle = metadata?.helpTitle || metadata?.title || 'Events Timeline';
  const helpAiContext =
    Object.prototype.hasOwnProperty.call(metadata || {}, 'aiContext')
      ? metadata.aiContext
      : HelpComponent.aiContext;
  const widgetTitle = metadata?.title || 'Events Timeline';
  const widgetDescription =
    metadata?.description ||
    "This widget displays a timeline of key events and actions related to the applicant's case, including status changes, messages, and other important updates.";

  const loadEvents = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!caseId) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/cases/' + caseId + '/events');
      if (!res.ok) throw new Error('Failed to fetch events');
      const data = await res.json();
      setEvents(Array.isArray(data) ? data : []);
    } catch (_) {
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    const handler = event => {
      const targetCaseId = event?.detail?.caseId;
      if (!caseId) return;
      if (targetCaseId && Number(targetCaseId) !== Number(caseId)) return;
      loadEvents({ silent: true });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('case-events-refresh', handler);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('case-events-refresh', handler);
      }
    };
  }, [caseId, loadEvents]);

  const parseEventDate = useCallback((value) => {
    if (!value) return null;
    const raw = String(value);
    const direct = new Date(raw);
    if (!Number.isNaN(direct.getTime())) return direct;
    const withZ = new Date(`${raw}Z`);
    if (!Number.isNaN(withZ.getTime())) return withZ;
    return null;
  }, []);

  const decoratedEvents = useMemo(() => events.map(decorateEvent), [events]);

  const filteredEvents = decoratedEvents.filter(item => {
    if (!filteringText) return true;
    const text = filteringText.toLowerCase();
    const eventDate = parseEventDate(item.created_at);
    const parts = [
      eventDate ? formatDateTime(eventDate).toLowerCase() : '',
      item.event_type_label ? item.event_type_label.toLowerCase() : '',
      item.event_type ? item.event_type.toLowerCase() : '',
      item.displayMessage ? item.displayMessage.toLowerCase() : '',
      item.actorDisplay ? item.actorDisplay.toLowerCase() : ''
    ];
    return parts.some(part => part.includes(text));
  });

  const columnDefinitions = [
    {
      id: 'date',
      header: 'Date/Time',
      sortingField: 'created_at',
      cell: item => {
        const d = parseEventDate(item.created_at);
        return d ? formatDateTime(d) : '';
      }
    },
    {
      id: 'type',
      header: 'Event Type',
      cell: item => <StatusIndicator type={item.alert_variant || 'info'}>{item.event_type_label || item.event_type}</StatusIndicator>
    },
    {
      id: 'data',
      header: 'Event Data',
      cell: item => {
        const isReminder =
          item?.event_type && item.event_type.startsWith('reminder_') && item?.event_data?.reminder_id;
        if (item.event_type?.startsWith('appeal_') && item.event_data?.appeal_id) {
          const href = buildNotificationTargetPath({ caseId: item.case_id, applicationId: item.event_data.application_id, appealId: item.event_data.appeal_id });
          return <SpaceBetween size="xxs"><div>{item.displayMessage || ''}</div>{href && <Link href={href}>View appeal</Link>}</SpaceBetween>;
        }
        if (!isReminder) {
          return item.displayMessage || '';
        }
        const trackingId = item.event_data?.tracking_id || item.tracking_id;
        const caseId = item.event_data?.case_id || item.case_id;
        const href = caseId
          ? `/application-case/${caseId}`
          : trackingId
            ? `/application-case/${trackingId}`
            : null;
        const detailText =
          item.event_type === 'reminder_due'
            ? item.reminderDetails || item.displayMessage || ''
            : item.displayMessage || '';
        return (
          <SpaceBetween size="xxs">
            <div>{detailText}</div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              {trackingId ? <Badge color="blue">Tracking ID {trackingId}</Badge> : null}
              {item.event_data?.category ? <Badge color="grey">{item.event_data.category}</Badge> : null}
              {href ? (
                <Link href={href}>View {caseId ? 'case' : 'application'}</Link>
              ) : null}
            </div>
          </SpaceBetween>
        );
      }
    },
    {
      id: 'actor',
      header: 'Actor',
      cell: item => item.actorDisplay || ''
    }
  ];

  const currentSortingColumn = columnDefinitions.find(
    col => col.sortingField === sortingColumn.sortingField
  ) || columnDefinitions[0];

  const sortedEvents = [...filteredEvents].sort((a, b) => {
    if (currentSortingColumn.sortingField === 'created_at') {
      const aTime = parseEventDate(a.created_at)?.getTime() ?? 0;
      const bTime = parseEventDate(b.created_at)?.getTime() ?? 0;
      if (aTime === bTime) return 0;
      return isDescending ? bTime - aTime : aTime - bTime;
    }
    return 0;
  });

  const handleDownloadCsv = useCallback(() => {
    if (!sortedEvents.length || csvGenerating) return;
    setCsvGenerating(true);
    try {
      const header = ['Date/Time', 'Event Type', 'Event Data', 'Actor'];
      const rows = sortedEvents.map(item => {
        const dateStr = item.created_at ? formatDateTime(item.created_at) : '';
        const typeStr = item.event_type_label || item.event_type || '';
        const dataStr = (item.displayMessage || '').replace(/\r?\n/g, ' ').trim();
        const actorStr = item.actorDisplay || '';
        return [dateStr, typeStr, dataStr, actorStr];
      });
      const csv = [header, ...rows]
        .map(cols => cols.map(value => `"${String(value).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'case-events.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download CSV', err);
    } finally {
      setCsvGenerating(false);
    }
  }, [csvGenerating, sortedEvents]);

  return (
    <BoardItem
      header={
        <Header
          variant="h2"
          info={
            toggleHelpPanel ? (
              <Link
                variant="info"
                onFollow={() =>
                  toggleHelpPanel(
                    <HelpComponent />,
                    helpTitle,
                    helpAiContext
                  )
                }
              >
                Info
              </Link>
            ) : undefined
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                onClick={handleDownloadCsv}
                disabled={!sortedEvents.length || csvGenerating}
              >
                Download .csv
              </Button>
              <Button
                variant="icon"
                iconName="refresh"
                ariaLabel="Refresh events"
                onClick={() => loadEvents({ silent: false })}
                disabled={loading || !caseId}
              />
            </SpaceBetween>
          }
        >
          <Hotspot hotspotId="app-workspace-events-timeline" direction="right" />
          {widgetTitle}
        </Header>
      }
      i18nStrings={{
        dragHandleAriaLabel: 'Drag handle',
        dragHandleAriaDescription: 'Use Space or Enter to activate drag, arrow keys to move, Space or Enter to drop.',
        resizeHandleAriaLabel: 'Resize handle',
        resizeHandleAriaDescription: 'Use Space or Enter to activate resize, arrow keys to resize, Space or Enter to finish.'
      }}
      settings={
        <ButtonDropdown
          items={[{ id: 'remove', text: 'Remove' }]}
          ariaLabel="Board item settings"
          variant="icon"
          onItemClick={() => actions?.removeItem?.()}
        />
      }
    >
      <Box variant="small" margin={{ bottom: 's' }}>
        {widgetDescription}
      </Box>
      {loading ? (
        <Box textAlign="center" padding="m"><Spinner /> Loading events...</Box>
      ) : error ? (
        <Box color="error" textAlign="center">{error}</Box>
      ) : (
        <SpaceBetween size="m">
          <TextFilter
            filteringText={filteringText}
            onChange={({ detail }) => setFilteringText(detail.filteringText)}
            filteringPlaceholder="Find events"
            countText={
              filteringText
                ? `${sortedEvents.length} match${sortedEvents.length === 1 ? '' : 'es'}`
                : ''
            }
          />
          <Table
            columnDefinitions={columnDefinitions.map(col =>
              col.sortingField === currentSortingColumn.sortingField
                ? {
                    ...col,
                    isSorted: true,
                    isSortedDescending: isDescending
                  }
                : col
            )}
            items={sortedEvents}
            sortingColumn={currentSortingColumn}
            sortingDescending={isDescending}
            onSortingChange={({ detail }) => {
              if (detail.sortingColumn.sortingField === currentSortingColumn.sortingField) {
                setIsDescending(prev => !prev);
              } else {
                setSortingColumn({ sortingField: detail.sortingColumn.sortingField });
                setIsDescending(true);
              }
            }}
            variant="embedded"
            stickyHeader
            resizableColumns
            empty={<div>No events</div>}
          />
        </SpaceBetween>
      )}
    </BoardItem>
  );
};

export default ApplicationEvents;
